import { useState } from "react";
import Link from "next/link";
import TagManager from "react-gtm-module";
import { useTranslation } from "react-i18next";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { sendContact } from "@/data";
import { SendFormWithCaptcha } from "@/helpers/sendFormWithCaptcha";
import { FormContent } from "@/domains/freeTrial";
import { FormInfo } from "@/interfaces";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TextField from "@mui/material/TextField";
import { ButtonAlert } from "@/components/ui";
import { FullWidthLayout } from "@/layouts";
import { TableDescription } from "./TableDescription";
import { CustomCheckbox } from "@/components/ui/checkbox";
import * as Styled from "./signUp.styles";
import { GooglePolicy } from "@/components/ui/googlePolicy";

export const SignUp = () => {
  const { t } = useTranslation("global");
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [checked, setChecked] = useState({
    freeTrial: false,
    policy: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [alertState, setAlertState] = useState("");
  const [showAlert, setShowAlert] = useState(false);

  const [formInfo, setFormInfo] = useState({
    [FormContent.firstName]: "",
    [FormContent.lastName]: "",
    [FormContent.company]: "",
    [FormContent.companyAddress]: "",
    [FormContent.email]: "",
    [FormContent.city]: "",
    [FormContent.country]: "",
    [FormContent.postalCode]: "",
  });

  const userInfo = {
    firstName: formInfo.firstName,
    lastName: formInfo.lastName,
    company: formInfo.company,
    companyAddress: formInfo.companyAddress,
    email: formInfo.email,
    city: formInfo.city,
    country: formInfo.country,
    postalCode: formInfo.postalCode,
  };

  const handleOnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    //validate that checkbox is checked
    if (!checked) {
      setMessage(t("freeTrialSignUp.freeTrialSignUp.errorCheck")!);
      setAlertState("checkbox");
      setShowAlert(true);
      return;
    }
    setIsLoading(true);

    //send form with captcha
    SendFormWithCaptcha(executeRecaptcha).then((res: any) => {
      TagManager.dataLayer({
        dataLayer: {
          category: "forms",
          event: "trial-form-submit",
          action: "trial-form-submit",
        },
      });
      const data = {
        service_id: "service_tk4roz4",
        template_id: "template_gzs5oqg",
        user_id: "n2IOOPOiPZCuhSeej",
        template_params: userInfo,
        g_recaptcha_response: res,
      };
      //helper function to send form
      sendContact(data)
        .then((res) => {
          if (res.status === 200) {
            setMessage(t("freeTrialSignUp.freeTrialSignUp.success")!);
            setAlertState("success");
            setShowAlert(true);
            setFormInfo({
              firstName: "",
              lastName: "",
              company: "",
              companyAddress: "",
              email: "",
              city: "",
              country: "",
              postalCode: "",
            });
            setTimeout(() => {
              setShowAlert(false);
            }, 5000);
          } else {
            setMessage(t("freeTrialSignUp.freeTrialSignUp.error")!);
            setAlertState("error");
            setShowAlert(true);
            setTimeout(() => {
              setShowAlert(false);
            }, 5000);
          }
          setIsLoading(false);
        })
        .catch((err) => {
          setMessage(t("freeTrialSignUp.freeTrialSignUp.error")!);
          setAlertState("error");
          setIsLoading(false);
          setShowAlert(true);
          setTimeout(() => {
            setShowAlert(false);
          }, 5000);
        });
    });
  };

  return (
    <FullWidthLayout id="signUp" bgColor="var(--background-light)">
      <Styled.SignUpContainer>
        {/* <Slider />  when we have customers it will be used*/}
        <TableDescription />
        <Styled.FormContainer>
          <Styled.InfoBox>
            <h1 className="display-medium weight-medium">{t("freeTrialSignUp.title")}</h1>
            <p className="body-medium">{t("freeTrialSignUp.description")}</p>
          </Styled.InfoBox>
          {/* Form */}
          <form onSubmit={handleOnSubmit}>
            <Styled.FormBox>
              {Object.keys(FormContent).map((item) => (
                <TextField
                  key={item}
                  id={item}
                  error={formInfo[item as keyof FormInfo] === "" && showAlert}
                  required
                  label={t(`freeTrialSignUp.form.${item}`)}
                  variant="filled"
                  color="primary"
                  sx={Styled.formWidth}
                  type={item === "email" ? "email" : "text"}
                  onChange={(e) => {
                    setFormInfo({
                      ...formInfo,
                      [item]: e.target.value,
                    });
                  }}
                  value={formInfo[item as keyof FormInfo]}
                />
              ))}
            </Styled.FormBox>

            <GooglePolicy mobile={"100%"} />

            <Styled.FooterBox>
              <CustomCheckbox
                text={"freeTrialSignUp.agreement"}
                textLink={"freeTrialSignUp.agreementTerms"}
                link={"/freetrialagreement"}
                id="checkbox-agreement"
                checked={checked.freeTrial}
                onChange={() => setChecked({ ...checked, freeTrial: !checked.freeTrial })}
              />
              <CustomCheckbox
                text={"freeTrialSignUp.policy"}
                textLink={"freeTrialSignUp.policyTerms"}
                link={"/policy"}
                id="checkbox-agreement"
                checked={checked.policy}
                onChange={() => setChecked({ ...checked, policy: !checked.policy })}
              />
              <Styled.BtnContainer>
                <ButtonAlert
                  type="submit"
                  color="primary"
                  size="medium"
                  isLoading={isLoading}
                  message={message}
                  alertState={alertState}
                  showAlert={showAlert}
                  sx={Styled.sxBtn}
                  endIcon={<ArrowForwardIcon />}
                  disabled={!checked.freeTrial || !checked.policy}
                >
                  {t("freeTrialSignUp.button")}
                </ButtonAlert>

                <p className="body-medium" style={{ alignSelf: "flex-start" }}>
                  {t("freeTrialSignUp.questions")}{" "}
                  <Link href="/contact" passHref>
                    <span
                      className="body-medium weight-medium"
                      style={{
                        textDecoration: "underline",
                      }}
                    >
                      {t("freeTrialSignUp.contact")}
                    </span>
                  </Link>
                </p>
              </Styled.BtnContainer>
            </Styled.FooterBox>
          </form>
        </Styled.FormContainer>
      </Styled.SignUpContainer>
    </FullWidthLayout>
  );
};
